import { isNil } from 'es-toolkit';

import urlFrom from '@savvy/utils/error-handling/url-from';

/**
 * Validates that a URL is safe to send authentication tokens to.
 * Only allows URLs that resolve to the configured apiHost origin.
 *
 * Safe URLs:
 * - Relative paths starting with "/" (e.g., "/exports/file.csv")
 * - Absolute URLs matching apiHost origin (e.g., "https://api.savvywealth.com/exports/file.csv")
 *
 * Unsafe URLs (rejected):
 * - Absolute URLs to other domains (e.g., "https://evil.com/steal-token")
 * - Protocol-relative URLs (e.g., "//evil.com")
 * - Non-HTTP protocols (e.g., "javascript:", "data:", "file:")
 * - null/undefined/empty strings
 * - Strings containing any whitespace characters
 *
 * @param url - The URL to validate (can be relative or absolute)
 * @param apiHost - The trusted API host origin (e.g., "https://api.savvywealth.com")
 * @returns true if safe to send auth tokens, false otherwise
 */
export function isSafeDownloadUrl(url: string | null | undefined, apiHost: string | null | undefined): boolean {
  // Reject null/undefined/empty/containing whitespace
  if (isNil(url) || url.length === 0 || /\s/.test(url)) {
    return false;
  }

  if (isNil(apiHost) || apiHost.length === 0 || /\s/.test(apiHost)) {
    return false;
  }

  // Reject protocol-relative URLs (e.g., "//evil.com")
  if (url.startsWith('//')) {
    return false;
  }

  // Parse apiHost to get trusted origin
  const apiHostResult = urlFrom(apiHost);
  if (apiHostResult.isErr()) {
    return false;
  }
  const trustedOrigin = apiHostResult.value.origin;

  // Check if it's an absolute URL
  const absoluteResult = urlFrom(url);
  if (absoluteResult.isOk()) {
    // For absolute URLs, verify origin matches apiHost
    return absoluteResult.value.origin === trustedOrigin;
  }

  // For relative paths, must start with "/" to be safe
  if (!url.startsWith('/')) {
    return false;
  }

  // Parse as relative URL to validate structure
  const relativeResult = urlFrom(url, trustedOrigin);
  return relativeResult.isOk();
}
